import styled, { keyframes } from 'styled-components';

const breakpoints = {
  mobile: '768px',
  tablet: '1024px',
  desktop: '1440px',
};

const bgParallax = keyframes`
  from {
    background-position: 0px 100px;
  }
  to {
    background-position: left;
  }
`;

const contentParallax = keyframes`
  from {
    transform: translate(-80px, 0%);
    opacity: 0;
  }
  to {
    transform: translate(0, 0);
    opacity: 1;
  }
`;

const logoParallax = keyframes`
  from {
    transform: translate(0, -50%);
    opacity: 0;
  }
  to {
    transform: translate(0, 0);
    opacity: 1;
  }
`;

const cardParallax = keyframes`
  from {
    transform: translate(120px, -50%) scale(1);
  }
  to {
    transform: translate(0, -50%) scale(1);
  }
`;

export const Wrapper = styled.div`
  position: relative;
  max-width: 1280px;
  width: 100%;
  height: 832px;
  overflow: hidden;
  border-radius: 40px;
  display: flex;
  margin: 0 auto;

  @media (max-width: ${breakpoints.mobile}) {
    max-width: 381px;
    height: 721px;
    border-radius: 30px;
  }
`;

export const Background = styled.div`
  flex: 1;
  background: ${({ bgColor }) => bgColor};
  background-image: url(${({ image }) => image});
  background-size: 832px;
  background-position: left;
  transition: background 0.2s ease;
  position: relative;
  background-repeat: no-repeat;

  animation: ${bgParallax} 1s ease;

  @media (max-width: ${breakpoints.mobile}) {
    background-size: 246px;
    background-image: url(${({ mobImage }) => mobImage});
  }
`;

export const Overlay = styled.div`
  position: absolute;
  inset: 0;
`;

export const Content = styled.div`
  position: absolute;
  left: 60px;
  top: 20%;
  color: white;
  max-width: 650px;
  z-index: 2;

  animation: ${contentParallax} 0.9s cubic-bezier(0.22, 0.61, 0.36, 1);

  @media (max-width: ${breakpoints.mobile}) {
    max-width: 317px;
    left: 20px;
    top: 10%;
  }

  h4 {
    font-size: 2rem;
    line-height: 100%;
    text-transform: uppercase;

    @media (max-width: ${breakpoints.mobile}) {
      font-size: 1.25rem;
    }
  }

  h2 {
    font-size: 3.75rem;
    line-height: 100%;
    text-transform: uppercase;

    @media (max-width: ${breakpoints.mobile}) {
      font-size: 2.5rem;
    }
  }

  p {
    font-size: 1rem;
    line-height: 1.9;

    @media (max-width: ${breakpoints.mobile}) {
      font-size: 0.625rem;
    }
  }

  a {
    color: #fff;

    p {
      font-size: 2.5rem;
      margin-bottom: 0;
      text-transform: uppercase;

      @media (max-width: ${breakpoints.mobile}) {
        font-size: 1.25rem;
      }
    }
  }
`;

export const FloatingCard = styled.div`
  position: absolute;
  right: 80px;
  top: 50%;
  transform: translateY(-50%);
  width: 488px;
  height: 388px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  background-image: url(${({ thumbImage }) => thumbImage});
  background-repeat: no-repeat;
  background-size: 104%;
  background-position: top left;

  animation: ${cardParallax} 1s cubic-bezier(0.22, 0.61, 0.36, 1);

  @media (max-width: ${breakpoints.mobile}) {
    width: 244px;
    height: 194px;
    background-size: 110%;
    background-position: center;
    right: 20px;
    top: 85%;
  }
`;

export const Controls = styled.div`
  position: absolute;
  bottom: 20%;
  right: 30%;
  display: flex;
  gap: 20px;
  z-index: 2;

  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column-reverse;
    bottom: 10%;
    right: 75%;
  }
`;

export const Button = styled.div`
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: transparent;
  color: white;
  cursor: pointer;

  @media (max-width: ${breakpoints.mobile}) {
    width: 41px;
    height: 41px;
  }

  img {
    width: 100%;
    height: auto;
    display: block;

    &.prev-btn {
      transform: rotate(180deg);
    }
  }

  &:hover {
    img {
      filter: drop-shadow(0px 0px 10px rgba(255, 255, 255, 1))
        drop-shadow(0px 0px 4px rgba(255, 255, 255, 1));
    }
  }
`;

export const LogoContent = styled.div`
  display: flex;
  align-items: center;
  gap: 3rem;

  position: absolute;
  left: 80px;
  bottom: 10%;

  animation: ${logoParallax} 0.9s cubic-bezier(0.22, 0.61, 0.36, 1);

  @media (max-width: ${breakpoints.mobile}) {
    max-width: 100% !important;
    flex-wrap: wrap;
    left: 20px;
    gap: 1.5rem;
    bottom: 35%;
  }

  img {
    display: block;
    height: 50px;
    width: auto;

    @media (max-width: ${breakpoints.mobile}) {
      height: 30px;
    }

    &.logo-paramount {
      height: 80px;

      @media (max-width: ${breakpoints.mobile}) {
        height: 30px;
      }
    }

    &.logo-gaumon {
      height: 60px;

      @media (max-width: ${breakpoints.mobile}) {
        height: 37px;
      }
    }

    &.logo-apple {
      height: 61px;

      @media (max-width: ${breakpoints.mobile}) {
        height: 41px;
      }
    }

    &.logo-disney {
      height: 115px;

      @media (max-width: ${breakpoints.mobile}) {
        height: 67px;
      }
    }

    &.logo-univision {
      height: 90px;

      @media (max-width: ${breakpoints.mobile}) {
        height: 68px;
      }
    }

    &.logo-amzon {
      height: 60px;

      @media (max-width: ${breakpoints.mobile}) {
        height: 35px;
      }
    }

    &.logo-studios {
      height: 44px;

      @media (max-width: ${breakpoints.mobile}) {
        height: 24px;
      }
    }

    &.logo-prime {
      height: 60px;

      @media (max-width: ${breakpoints.mobile}) {
        height: 33px;
      }
    }

    &.logo-kuter {
      height: 53px;

      @media (max-width: ${breakpoints.mobile}) {
        height: 27px;
      }
    }
  }
`;
